export type CardSection = 'quick' | 'computed' | 'charts';
export type CardLayout = { order: string[]; hidden: Set<string> };

const DEFAULT_CARD_LAYOUTS: Record<CardSection, { order: string[]; hidden: string[]; hideUnlisted: boolean }> = {
  quick: {
    order: [],
    hidden: ['max-players', 'active-players', 'bots'],
    hideUnlisted: false,
  },
  computed: {
    order: [
      'computed.players-stats',
      'computed.winner-sim',
      'computed.top-conquests',
      'computed.trade-king',
      'computed.train-king',
      'computed.conquests-total',
      'computed.gold-train-total',
      'computed.gold-trade-ships-total',
      'computed.runtime',
      'computed.avg-tick-exec',
      'computed.tick-exec-p95',
      'computed.tick-exec-max',
      'computed.intents-total',
      'computed.intents-tick',
      'computed.hashes-computed-expected',
      'computed.team-count',
      'computed.team-colors',
      'computed.timings',
    ],
    hidden: [
      'computed.players-stats',
      'computed.runtime',
      'computed.intents-total',
      'computed.intents-tick',
      'computed.hashes-computed-expected',
      'computed.team-count',
      'computed.team-colors',
      'computed.timings',
    ],
    hideUnlisted: false,
  },
  charts: {
    order: [
      'chart.tick-execution-time-ms',
      'chart.intents-per-tick',
      'chart.players-alive-humans',
      'chart.territory-owned-tiles',
      'chart.troops-current',
      'chart.gold-current-k',
      'chart.conquests-cumulative',
      'chart.gold-earned-k-work',
      'chart.gold-earned-k-war',
      'chart.attacks-sent-cumulative-troops',
      'chart.gold-earned-k-trade',
      'chart.gold-earned-k-train',
      'chart.gold-donations-k-sent',
      'chart.gold-donations-k-received',
      'chart.troop-donations-sent',
      'chart.troop-donations-received',
      'chart.attacks-received-cumulative-troops',
      'chart.attacks-cancelled-cumulative-troops',
      'chart.boats-trade-sent',
      'chart.boats-trade-arrived',
      'chart.boats-trade-captured',
      'chart.boats-trade-destroyed',
      'chart.boats-transport-sent',
      'chart.boats-transport-arrived',
      'chart.boats-transport-arrived-excluding-returns',
      'chart.boats-transport-returned-cancelled',
      'chart.bombs-abomb-launch',
      'chart.bombs-abomb-land',
      'chart.bombs-abomb-intercept',
      'chart.bombs-hbomb-launch',
      'chart.bombs-mirv-launch',
      'chart.units-city-built',
      'chart.units-port-built',
      'chart.units-factory-built',
      'chart.units-warship-built',
    ],
    hidden: ['chart.gold-earned-k-work', 'chart.intents-per-tick'],
    hideUnlisted: false,
  },
};

export function cardLayoutStorageKey(section: CardSection) {
  return `ofra.cardLayout.${section}`;
}

export function slugifyCardId(input: string) {
  return (
    String(input || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'card'
  );
}

function loadCardLayout(section: CardSection): CardLayout {
  try {
    const raw = localStorage.getItem(cardLayoutStorageKey(section));
    if (!raw) {
      const def = DEFAULT_CARD_LAYOUTS[section];
      return { order: def.order.slice(), hidden: new Set(def.hidden) };
    }
    const parsed = JSON.parse(raw) as { order?: unknown; hidden?: unknown };
    const order = Array.isArray(parsed?.order) ? parsed.order.filter((x) => typeof x === 'string') : [];
    const hiddenArr = Array.isArray(parsed?.hidden) ? parsed.hidden.filter((x) => typeof x === 'string') : [];
    return { order, hidden: new Set(hiddenArr) };
  } catch {
    const def = DEFAULT_CARD_LAYOUTS[section];
    return { order: def.order.slice(), hidden: new Set(def.hidden) };
  }
}

function saveCardLayout(section: CardSection, layout: CardLayout) {
  try {
    localStorage.setItem(
      cardLayoutStorageKey(section),
      JSON.stringify({ order: Array.from(layout.order || []), hidden: Array.from(layout.hidden || []) }),
    );
  } catch {
    // ignore (storage may be disabled)
  }
}

let availablePopoverEl: HTMLElement | null = null;
let availablePopoverSection: CardSection | null = null;
let availablePopoverAnchor: HTMLElement | null = null;
let availablePopoverOnDocMouseDown: ((e: MouseEvent) => void) | null = null;

function getGridElForSection(section: CardSection): HTMLElement | null {
  if (section === 'quick') return document.getElementById('statsGrid');
  if (section === 'computed') return document.getElementById('computedGrid');
  return document.getElementById('chartsGrid');
}

function cardControlsIds(section: CardSection) {
  if (section === 'quick') return { available: 'quickCardsAvailable', reset: 'quickCardsReset' };
  if (section === 'computed') return { available: 'computedCardsAvailable', reset: 'computedCardsReset' };
  return { available: 'chartCardsAvailable', reset: 'chartCardsReset' };
}

export function updateAvailableButton(section: CardSection) {
  const ids = cardControlsIds(section);
  const btn = document.getElementById(ids.available) as HTMLButtonElement | null;
  if (!btn) return;
  const n = loadCardLayout(section).hidden.size;
  btn.textContent = `${n} available`;
  btn.disabled = n === 0;
}

export function closeAvailablePopover() {
  if (availablePopoverOnDocMouseDown) document.removeEventListener('mousedown', availablePopoverOnDocMouseDown, true);
  availablePopoverOnDocMouseDown = null;
  availablePopoverEl?.remove();
  availablePopoverEl = null;
  availablePopoverSection = null;
  availablePopoverAnchor = null;
}

export function refreshAvailablePopover() {
  if (!availablePopoverEl || !availablePopoverSection) return;
  const section = availablePopoverSection;
  const grid = getGridElForSection(section);
  if (!grid) return;
  const layout = loadCardLayout(section);

  const titleById = new Map<string, string>();
  for (const el of Array.from(grid.querySelectorAll<HTMLElement>('.ofra-card[data-card-id]'))) {
    const id = el.dataset.cardId;
    if (!id) continue;
    const t = el.dataset.cardTitle || el.querySelector('h4')?.textContent || '';
    if (t) titleById.set(id, t);
  }

  const entries = Array.from(layout.hidden)
    .map((id) => ({ id, title: titleById.get(id) || id }))
    .sort((a, b) => a.title.localeCompare(b.title));

  availablePopoverEl.innerHTML = '';

  const header = document.createElement('div');
  header.className = 'ofra-available-header';
  const h = document.createElement('div');
  h.textContent = 'Hidden cards';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'ofra-available-close';
  closeBtn.textContent = '×';
  closeBtn.onclick = closeAvailablePopover;
  header.appendChild(h);
  header.appendChild(closeBtn);
  availablePopoverEl.appendChild(header);

  if (!entries.length) {
    const empty = document.createElement('div');
    empty.className = 'ofra-available-empty';
    empty.textContent = 'No hidden cards';
    availablePopoverEl.appendChild(empty);
    return;
  }

  const list = document.createElement('div');
  list.className = 'ofra-available-list';
  for (const it of entries) {
    const row = document.createElement('div');
    row.className = 'ofra-available-row';
    const name = document.createElement('div');
    name.className = 'ofra-available-name';
    name.textContent = it.title;
    name.title = it.title;

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'ofra-available-add';
    add.textContent = 'Add';
    add.onclick = () => {
      const next = loadCardLayout(section);
      if (DEFAULT_CARD_LAYOUTS[section].hideUnlisted && !next.order.includes(it.id)) next.order.push(it.id);
      next.hidden.delete(it.id);
      saveCardLayout(section, next);
      applyCardLayoutToGrid(section, grid);
      updateAvailableButton(section);
      refreshAvailablePopover();
    };

    row.appendChild(name);
    row.appendChild(add);
    list.appendChild(row);
  }
  availablePopoverEl.appendChild(list);
}

export function openAvailablePopover(section: CardSection, anchor: HTMLElement) {
  if (availablePopoverEl && availablePopoverSection === section && availablePopoverAnchor === anchor) {
    closeAvailablePopover();
    return;
  }

  closeAvailablePopover();

  const pop = document.createElement('div');
  pop.id = 'ofraAvailablePopover';
  pop.className = 'ofra-available-popover';
  pop.setAttribute('role', 'dialog');
  pop.setAttribute('aria-label', 'Hidden cards');
  document.body.appendChild(pop);

  availablePopoverEl = pop;
  availablePopoverSection = section;
  availablePopoverAnchor = anchor;

  const place = () => {
    if (!availablePopoverEl || !availablePopoverAnchor) return;
    const r = availablePopoverAnchor.getBoundingClientRect();
    const pad = 10;
    const w = availablePopoverEl.offsetWidth || 320;
    const x = Math.max(pad, Math.min(window.scrollX + r.left, window.scrollX + window.innerWidth - w - pad));
    const y = window.scrollY + r.bottom + 8;
    availablePopoverEl.style.left = x + 'px';
    availablePopoverEl.style.top = y + 'px';
  };

  refreshAvailablePopover();
  place();
  window.setTimeout(place, 0);

  availablePopoverOnDocMouseDown = (e: MouseEvent) => {
    const t = e.target as Node | null;
    if (!t) return;
    if (availablePopoverEl?.contains(t)) return;
    if (availablePopoverAnchor?.contains(t)) return;
    closeAvailablePopover();
  };
  document.addEventListener('mousedown', availablePopoverOnDocMouseDown, true);
}

export function decorateCardEl(section: CardSection, card: HTMLElement, cardId: string, title?: string) {
  card.classList.add('ofra-card');
  card.dataset.cardId = cardId;
  if (title) card.dataset.cardTitle = title;
  if (card.querySelector('.ofra-card-actions')) return;

  const actions = document.createElement('div');
  actions.className = 'ofra-card-actions';

  const drag = document.createElement('button');
  drag.type = 'button';
  drag.className = 'ofra-card-action ofra-card-drag';
  drag.title = 'Drag to reorder';
  drag.setAttribute('aria-label', 'Drag to reorder');
  drag.setAttribute('draggable', 'true');
  drag.textContent = '⠿';

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'ofra-card-action ofra-card-remove';
  remove.dataset.cardAction = 'remove';
  remove.title = 'Remove card';
  remove.setAttribute('aria-label', 'Remove card');
  remove.textContent = '×';

  actions.appendChild(drag);
  actions.appendChild(remove);
  card.appendChild(actions);
}

export function applyCardLayoutToGrid(section: CardSection, grid: HTMLElement) {
  const layout = loadCardLayout(section);
  const cards = Array.from(grid.querySelectorAll<HTMLElement>('.ofra-card[data-card-id]'));

  const cfg = DEFAULT_CARD_LAYOUTS[section];
  if (cfg.hideUnlisted) {
    const listed = new Set(layout.order);
    let changed = false;
    for (const card of cards) {
      const id = card.dataset.cardId;
      if (!id) continue;
      if (!listed.has(id) && !layout.hidden.has(id)) {
        layout.hidden.add(id);
        changed = true;
      }
    }
    if (changed) saveCardLayout(section, layout);
  }

  for (const card of cards) {
    const id = card.dataset.cardId;
    if (!id) continue;
    if (layout.hidden.has(id)) card.classList.add('ofra-card-hidden');
    else card.classList.remove('ofra-card-hidden');
  }

  if (!layout.order.length) return;
  const idx = new Map<string, number>();
  layout.order.forEach((id, i) => idx.set(id, i));
  const sorted = cards.slice().sort((a, b) => {
    const ai = a.dataset.cardId ? idx.get(a.dataset.cardId) : undefined;
    const bi = b.dataset.cardId ? idx.get(b.dataset.cardId) : undefined;
    const av = typeof ai === 'number' ? ai : Number.MAX_SAFE_INTEGER;
    const bv = typeof bi === 'number' ? bi : Number.MAX_SAFE_INTEGER;
    return av - bv;
  });
  for (const c of sorted) grid.appendChild(c);
}

function saveCardOrderFromGrid(section: CardSection, grid: HTMLElement) {
  const layout = loadCardLayout(section);
  const ids = Array.from(grid.querySelectorAll<HTMLElement>('.ofra-card[data-card-id]'))
    .map((el) => el.dataset.cardId)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);

  if (DEFAULT_CARD_LAYOUTS[section].hideUnlisted) {
    const allowed = new Set(layout.order);
    layout.order = ids.filter((id) => allowed.has(id));
  } else {
    layout.order = ids;
  }
  saveCardLayout(section, layout);
}

export function initCardGridInteractions(section: CardSection, grid: HTMLElement) {
  if (grid.dataset.ofraCardGridInit === '1') return;
  grid.dataset.ofraCardGridInit = '1';

  let dragging: HTMLElement | null = null;

  grid.addEventListener('dragstart', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t?.classList?.contains('ofra-card-drag')) return;
    const card = t.closest<HTMLElement>('.ofra-card[data-card-id]');
    if (!card || card.classList.contains('ofra-card-hidden')) return;

    dragging = card;
    dragging.classList.add('ofra-card-dragging');
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', card.dataset.cardId || '');
    }
  });

  grid.addEventListener('dragover', (e) => {
    if (!dragging) return;
    e.preventDefault();
    const over = (e.target as HTMLElement | null)?.closest<HTMLElement>('.ofra-card[data-card-id]');
    grid.querySelectorAll('.ofra-card-drop').forEach((el) => el.classList.remove('ofra-card-drop'));
    if (over && over !== dragging && !over.classList.contains('ofra-card-hidden')) over.classList.add('ofra-card-drop');
  });

  grid.addEventListener('drop', (e) => {
    if (!dragging) return;
    e.preventDefault();

    const over = (e.target as HTMLElement | null)?.closest<HTMLElement>('.ofra-card[data-card-id]');
    if (!over || over === dragging) {
      grid.appendChild(dragging);
    } else {
      const rect = over.getBoundingClientRect();
      const after = e.clientY > rect.top + rect.height / 2;
      grid.insertBefore(dragging, after ? over.nextSibling : over);
    }

    saveCardOrderFromGrid(section, grid);
  });

  grid.addEventListener('dragend', () => {
    if (dragging) dragging.classList.remove('ofra-card-dragging');
    dragging = null;
    grid.querySelectorAll('.ofra-card-drop').forEach((el) => el.classList.remove('ofra-card-drop'));
  });

  grid.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-card-action]') as HTMLElement | null;
    if (!btn) return;
    const action = btn.dataset.cardAction;
    if (!action) return;

    const card = btn.closest<HTMLElement>('.ofra-card[data-card-id]');
    const cardId = card?.dataset.cardId;
    if (!card || !cardId) return;

    if (action === 'remove') {
      const layout = loadCardLayout(section);
      layout.hidden.add(cardId);
      saveCardLayout(section, layout);
      card.classList.add('ofra-card-hidden');
      updateAvailableButton(section);
      refreshAvailablePopover();
    }
  });
}
